import React, { useState, useRef, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../contexts/ThemeContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiTrendingUp, FiBook, FiBarChart2, FiCpu, FiSettings, FiBell, FiSun, FiMoon, FiLogOut, FiUser, FiChevronDown, FiPower } = FiIcons

const Header = () => {
  const { user, profile, signOut } = useAuth()
  const { theme, setTheme } = useTheme()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const menuRef = useRef(null)

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: FiTrendingUp },
    { path: '/journal', label: 'Journal', icon: FiBook },
    { path: '/analytics', label: 'Analytics', icon: FiBarChart2 },
    { path: '/insights', label: 'AI Insights', icon: FiCpu },
    { path: '/settings', label: 'Settings', icon: FiSettings }
  ]

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark')
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSignOut = async () => {
    await signOut()
    navigate('/')
  }

  const handleExitApp = async () => {
    await signOut()
    if (window.matchMedia('(display-mode: standalone)').matches) {
      window.close()
    }
    navigate('/')
    window.location.reload()
  }

  // Get Avatar Icon Component
  const AvatarIcon = FiIcons[profile?.avatar_url] || FiUser;

  return (
    <motion.header 
      className="bg-white/80 dark:bg-[#0b0f1a]/80 backdrop-blur-xl sticky top-0 z-[60] border-b border-lux-light-border dark:border-white/5"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/dashboard" className="flex items-center space-x-3 group relative z-50">
            <div className="bg-primary-gradient p-2 rounded-xl shadow-lg shadow-lux-primary/20 group-hover:scale-110 transition-all duration-500">
              <SafeIcon icon={FiTrendingUp} className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">
              Trading<span className="text-lux-primary font-black">AI</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link 
                  key={item.path} 
                  to={item.path} 
                  className={`relative flex items-center space-x-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
                    isActive 
                      ? 'text-lux-primary' 
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  {isActive && (
                    <motion.div 
                      layoutId="nav-active"
                      className="absolute inset-0 bg-lux-primary/10 rounded-xl"
                      transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  <SafeIcon icon={item.icon} className="h-4 w-4 relative z-10" />
                  <span className="relative z-10">{item.label}</span>
                </Link>
              )
            })}
          </nav>

          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="flex items-center bg-gray-100/50 dark:bg-white/5 rounded-[1.25rem] p-1 border border-gray-200 dark:border-white/10 shadow-sm">
              <button 
                onClick={toggleTheme}
                className="p-2.5 rounded-[0.9rem] text-gray-400 hover:text-lux-primary hover:bg-white dark:hover:bg-white/10 transition-all group"
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={theme}
                    initial={{ opacity: 0, rotate: -90, scale: 0.5 }}
                    animate={{ opacity: 1, rotate: 0, scale: 1 }}
                    exit={{ opacity: 0, rotate: 90, scale: 0.5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <SafeIcon icon={theme === 'dark' ? FiSun : FiMoon} className="h-4 w-4" />
                  </motion.div>
                </AnimatePresence>
              </button>

              <button className="p-2.5 rounded-[0.9rem] text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white dark:hover:bg-white/10 transition-all">
                <SafeIcon icon={FiBell} className="h-4 w-4" />
              </button>
            </div>

            <div className="relative" ref={menuRef}>
              <button 
                onClick={() => setShowUserMenu(!showUserMenu)}
                className={`flex items-center space-x-3 p-1 pr-3 rounded-[1.5rem] transition-all duration-300 border ${
                  showUserMenu 
                    ? 'bg-gray-100 dark:bg-white/10 border-lux-primary/30 shadow-lg' 
                    : 'bg-transparent border-transparent hover:bg-gray-50 dark:hover:bg-white/5'
                }`}
              >
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-lux-primary to-lux-accent border border-white/20 flex items-center justify-center text-white shadow-md ring-2 ring-white/10 overflow-hidden">
                  <SafeIcon icon={AvatarIcon} className="h-5 w-5" />
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-[10px] font-black text-lux-primary uppercase tracking-[0.1em] leading-tight">
                    {profile?.trading_experience || 'Pro'}
                  </p>
                  <p className="text-xs font-bold text-gray-900 dark:text-white leading-tight">
                    {profile?.display_name?.split(' ')[0] || 'Trader'}
                  </p>
                </div>
                <SafeIcon 
                  icon={FiChevronDown} 
                  className={`h-3 w-3 text-gray-400 transition-transform duration-300 hidden sm:block ${showUserMenu ? 'rotate-180' : ''}`} 
                />
              </button>

              <AnimatePresence>
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute right-0 mt-3 w-64 bg-white dark:bg-[#0f172a] rounded-[2rem] shadow-[0_20px_50px_-12px_rgba(0,0,0,0.3)] border border-gray-100 dark:border-white/10 overflow-hidden"
                  >
                    <div className="p-5 border-b border-gray-100 dark:border-white/5 bg-gray-50/50 dark:bg-white/[0.02]">
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Authenticated Account</p>
                      <p className="text-sm font-bold text-gray-900 dark:text-white truncate">{user?.email}</p>
                    </div>
                    
                    <div className="p-2 space-y-1">
                      <Link 
                        to="/settings" 
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center space-x-3 px-4 py-3.5 rounded-2xl text-sm font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 hover:text-lux-primary transition-all group"
                      >
                        <div className="p-2 rounded-xl bg-gray-100 dark:bg-white/5 group-hover:bg-lux-primary/10 group-hover:text-lux-primary">
                          <SafeIcon icon={FiUser} className="h-4 w-4" />
                        </div>
                        <span>Account Settings</span>
                      </Link>

                      <button 
                        onClick={handleSignOut}
                        className="w-full flex items-center space-x-3 px-4 py-3.5 rounded-2xl text-sm font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 transition-all group"
                      >
                        <div className="p-2 rounded-xl bg-gray-100 dark:bg-white/5 group-hover:bg-gray-200 dark:group-hover:bg-white/10">
                          <SafeIcon icon={FiLogOut} className="h-4 w-4" />
                        </div>
                        <span>Sign Out Session</span>
                      </button>

                      <button 
                        onClick={handleExitApp}
                        className="w-full flex items-center space-x-3 px-4 py-3.5 rounded-2xl text-sm font-bold text-red-500 hover:bg-red-500/5 transition-all group"
                      >
                        <div className="p-2 rounded-xl bg-red-500/5 group-hover:bg-red-500/10">
                          <SafeIcon icon={FiPower} className="h-4 w-4" />
                        </div>
                        <span>Exit Application</span>
                      </button>
                    </div>

                    <div className="p-4 bg-lux-primary/5 mt-2">
                      <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-tighter">
                        <span className="text-gray-500">Premium Status</span>
                        <span className="text-lux-primary">Active</span>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </motion.header>
  )
}

export default Header